import React, { useEffect, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { customFieldService } from '../../services/custom-field.service'
import './InputCustomFieldsValues.scss'
import { InputSelect } from './InputSelect'

const FIXED_VALUE_MODE = 'fixed'
const DATE_NOW_EXPRESSION = '{{date.now}}'
const DATE_TOMORROW_EXPRESSION = '{{date + 1}}'
const DATE_YEAR_EXPRESSION = '{{date.year}}'
const DYNAMIC_DATE_EXPRESSION_PATTERN = /^\{\{\s*date(?:\.now|\s*[+-]\s*\d+)\s*\}\}$/
const DYNAMIC_DATE_DRAFT_PATTERN = /^\{\{\s*date\b/
const DYNAMIC_DATE_VALIDATION_DELAY_MS = 700
const EMPTY_CUSTOM_FIELD_VALUES: Record<number, any> = {}

interface InputCustomFieldsValuesProps {
  value?: Record<number, any>
  onChange?: (value: Record<number, any>) => void
  selectedFields?: number[]
  requiredFields?: number[]
  onRequiredFieldsChange?: (value: number[]) => void
  onRemoveSelectedField?: (fieldId: number) => void
  disabled?: boolean
}

export const InputCustomFieldsValues: React.FC<InputCustomFieldsValuesProps> = ({
  value = EMPTY_CUSTOM_FIELD_VALUES,
  onChange,
  selectedFields = [],
  requiredFields = [],
  onRequiredFieldsChange,
  onRemoveSelectedField,
  disabled = false,
}) => {
  const { t } = useTranslation()
  const [customFields, setCustomFields] = useState<any[]>([])
  const [customDynamicDateFields, setCustomDynamicDateFields] = useState<number[]>([])
  const [dynamicDateErrors, setDynamicDateErrors] = useState<Record<number, string>>({})

  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const response = await customFieldService.listAll()
        setCustomFields((response.results || []).filter((field: any) => !['separator', 'counter'].includes(field.data_type)))
      } catch (error) {}
    }
    loadCustomFields()
  }, [])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const nextErrors: Record<number, string> = {}

      selectedFields.forEach((fieldId) => {
        const field = customFields.find((item) => item.id === fieldId)
        const fieldValue = value[fieldId]
        const isCustomDynamicDate =
          field?.data_type === 'date' &&
          (customDynamicDateFields.includes(fieldId) || (typeof fieldValue === 'string' && DYNAMIC_DATE_DRAFT_PATTERN.test(fieldValue)))

        if (isCustomDynamicDate && (typeof fieldValue !== 'string' || !DYNAMIC_DATE_EXPRESSION_PATTERN.test(fieldValue.trim()))) {
          nextErrors[fieldId] = t('workflow_invalid_dynamic_date_expression', {
            dateNowExpression: DATE_NOW_EXPRESSION,
            dateAddExpression: '{{date + N}}',
            dateSubtractExpression: '{{date - N}}',
          })
        }
      })

      setDynamicDateErrors(nextErrors)
    }, DYNAMIC_DATE_VALIDATION_DELAY_MS)

    return () => window.clearTimeout(timeoutId)
  }, [customDynamicDateFields, customFields, selectedFields, t, value])

  const getCustomField = (id: number) => {
    return customFields.find((field) => field.id === id)
  }

  const handleFieldValueChange = (fieldId: number, newValue: any) => {
    setDynamicDateErrors((current) => {
      if (!current[fieldId]) return current

      const nextErrors = { ...current }
      delete nextErrors[fieldId]
      return nextErrors
    })

    const newValues = { ...value, [fieldId]: newValue }
    onChange?.(newValues)
  }

  const renderFieldInput = (fieldId: number) => {
    const field = getCustomField(fieldId)
    if (!field) return null

    const fieldValue = value[fieldId] ?? ''

    switch (field.data_type) {
      case 'string':
        return (
          <Form.Control
            type="text"
            value={fieldValue}
            onChange={(e) => handleFieldValueChange(fieldId, e.target.value)}
            placeholder={`${t('enter_value_for')} ${field.name}`}
            disabled={disabled}
          />
        )
      case 'date': {
        const isKnownDynamicDate = [DATE_NOW_EXPRESSION, DATE_TOMORROW_EXPRESSION].includes(fieldValue)
        const isCustomDynamicDate =
          customDynamicDateFields.includes(fieldId) ||
          (typeof fieldValue === 'string' && DYNAMIC_DATE_DRAFT_PATTERN.test(fieldValue) && !isKnownDynamicDate)
        const dateValueMode = isKnownDynamicDate ? fieldValue : isCustomDynamicDate ? 'dynamic' : FIXED_VALUE_MODE
        const dynamicDateError = dynamicDateErrors[fieldId]

        return (
          <div className="dynamic-custom-field-value">
            <div className="dynamic-custom-field-value__mode">
              <InputSelect
                value={dateValueMode}
                onChange={(nextMode) => {
                  setCustomDynamicDateFields((current) =>
                    nextMode === 'dynamic' ? Array.from(new Set([...current, fieldId])) : current.filter((id) => id !== fieldId)
                  )
                  handleFieldValueChange(fieldId, nextMode === FIXED_VALUE_MODE ? '' : nextMode === 'dynamic' ? '{{date + 0}}' : nextMode)
                }}
                items={[
                  { id: FIXED_VALUE_MODE, label: t('workflow_fixed_date') },
                  { id: DATE_NOW_EXPRESSION, label: t('workflow_date_when_process_runs') },
                  { id: DATE_TOMORROW_EXPRESSION, label: t('workflow_day_after_process_runs') },
                  { id: 'dynamic', label: t('workflow_dynamic_date_expression') },
                ]}
                bindLabel="label"
                allowNull={false}
                disabled={disabled}
              />
            </div>
            <div className="dynamic-custom-field-value__input">
              <Form.Control
                type={dateValueMode === FIXED_VALUE_MODE ? 'date' : 'text'}
                value={fieldValue}
                onChange={(e) => handleFieldValueChange(fieldId, e.target.value)}
                readOnly={dateValueMode !== FIXED_VALUE_MODE && dateValueMode !== 'dynamic'}
                disabled={disabled}
                isInvalid={!!dynamicDateError}
                aria-label={t('workflow_custom_field_value')}
              />
              {dynamicDateError && <Form.Control.Feedback type="invalid">{dynamicDateError}</Form.Control.Feedback>}
            </div>
          </div>
        )
      }
      case 'integer': {
        const usesDynamicYear = fieldValue === DATE_YEAR_EXPRESSION

        return (
          <div className="dynamic-custom-field-value">
            <div className="dynamic-custom-field-value__mode">
              <InputSelect
                value={usesDynamicYear ? DATE_YEAR_EXPRESSION : FIXED_VALUE_MODE}
                onChange={(nextMode) => handleFieldValueChange(fieldId, nextMode === FIXED_VALUE_MODE ? '' : nextMode)}
                items={[
                  { id: FIXED_VALUE_MODE, label: t('workflow_fixed_number') },
                  { id: DATE_YEAR_EXPRESSION, label: t('workflow_current_year') },
                ]}
                bindLabel="label"
                allowNull={false}
                disabled={disabled}
              />
            </div>
            <Form.Control
              type={usesDynamicYear ? 'text' : 'number'}
              value={fieldValue}
              onChange={(e) => handleFieldValueChange(fieldId, e.target.value === '' ? '' : parseInt(e.target.value))}
              placeholder={`${t('enter_value_for')} ${field.name}`}
              readOnly={usesDynamicYear}
              disabled={disabled}
              aria-label={t('workflow_custom_field_value')}
            />
          </div>
        )
      }
      case 'float':
        return (
          <Form.Control
            type="number"
            step="0.1"
            value={fieldValue}
            onChange={(e) => handleFieldValueChange(fieldId, parseFloat(e.target.value) || 0)}
            placeholder={`${t('enter_value_for')} ${field.name}`}
            disabled={disabled}
          />
        )
      case 'boolean':
        return (
          <Form.Check
            type="checkbox"
            checked={fieldValue}
            onChange={(e) => handleFieldValueChange(fieldId, e.target.checked)}
            label={field.name}
            disabled={disabled}
          />
        )
      case 'url':
        return (
          <Form.Control
            type="url"
            value={fieldValue}
            onChange={(e) => handleFieldValueChange(fieldId, e.target.value)}
            placeholder={`${t('enter_value_for')} ${field.name}`}
            disabled={disabled}
          />
        )
      case 'select':
        return (
          <Form.Select value={fieldValue} onChange={(e) => handleFieldValueChange(fieldId, e.target.value)} disabled={disabled}>
            <option value="">{t('select_option')}</option>
            {field.extra_data?.select_options?.map((option: any) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        )
      case 'counter':
        return <Form.Control type="text" value={fieldValue} readOnly disabled placeholder={t('counter_pending')} />
      case 'longtext':
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={fieldValue}
            onChange={(e) => handleFieldValueChange(fieldId, e.target.value)}
            placeholder={`${t('enter_value_for')} ${field.name}`}
            disabled={disabled}
          />
        )
      default:
        return (
          <Form.Control
            type="text"
            value={fieldValue}
            onChange={(e) => handleFieldValueChange(fieldId, e.target.value)}
            placeholder={`${t('enter_value_for')} ${field.name}`}
            disabled={disabled}
          />
        )
    }
  }

  const handleRequiredChange = (fieldId: number, checked: boolean) => {
    const next = checked ? Array.from(new Set([...(requiredFields || []), fieldId])) : (requiredFields || []).filter((id) => id !== fieldId)
    onRequiredFieldsChange?.(next)
  }

  return (
    <div className="list-group mt-3 selected-fields">
      {selectedFields.map((fieldId) => {
        const field = getCustomField(fieldId)
        if (!field) return null
        const isRequired = (requiredFields || []).includes(fieldId)

        return (
          <div key={fieldId} className="list-group-item d-flex justify-content-between align-items-center gap-2">
            <div className="flex-grow-1">{renderFieldInput(fieldId)}</div>
            {onRequiredFieldsChange && (
              <Button
                type="button"
                size="sm"
                variant={isRequired ? 'outline-danger' : 'outline-secondary'}
                className="required-toggle text-nowrap"
                active={isRequired}
                onClick={() => handleRequiredChange(fieldId, !isRequired)}
                disabled={disabled}
                aria-pressed={isRequired}
              >
                {isRequired ? t('assign_custom_field_required') : t('assign_custom_field_optional')}
              </Button>
            )}
            <Button type="button" variant="link" className="text-danger" onClick={() => onRemoveSelectedField?.(fieldId)} disabled={disabled}>
              <i className="bi bi-trash"></i>
            </Button>
          </div>
        )
      })}
    </div>
  )
}
